import { useEffect, useMemo, useState } from "react";
import { auth } from "./firebase";
import { BOOKING_SLOT_OPTIONS, buildDateSlotKey } from "./bookingSlots";

const CREATE_ORDER_URL =
  process.env.REACT_APP_CREATE_ORDER_URL ||
  "https://us-central1-petaar-3dcab.cloudfunctions.net/createOrder";
const VERIFY_PAYMENT_URL =
  process.env.REACT_APP_VERIFY_PAYMENT_URL ||
  "https://us-central1-petaar-3dcab.cloudfunctions.net/verifyPayment";
const REASSIGN_BOOKING_URL =
  process.env.REACT_APP_REASSIGN_BOOKING_URL ||
  "https://us-central1-petaar-3dcab.cloudfunctions.net/reassignBooking";
// CONSULTATION CHAT: Avoid client-side hardcoded payment key fallback.
const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID || "";

function normalizeConsultationMode(modeValue = "") {
  const mode = String(modeValue || "").trim().toLowerCase();
  if (mode === "audio" || mode === "video" || mode === "chat") return mode;
  return "chat";
}

function normalizeBookingType(value = "") {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized || "service_booking";
}

function resolveBookingIntent({
  service,
  bookingType,
  consultationMode,
  consultationTopic,
}) {
  const explicitType = normalizeBookingType(bookingType);
  if (explicitType === "vet_consultation") {
    return {
      bookingType: "vet_consultation",
      consultationMode: normalizeConsultationMode(
        consultationMode || service?.consultationMode || service?.mode || "",
      ),
      consultationTopic: String(
        consultationTopic || service?.consultationTopic || service?.title || "Online vet consultation",
      ).trim(),
    };
  }
  if (explicitType !== "service_booking") {
    return {
      bookingType: explicitType,
      consultationMode: "",
      consultationTopic: "",
    };
  }

  const category = String(service?.category || "").trim().toLowerCase();
  const title = String(service?.title || "").trim().toLowerCase();
  const topic = String(consultationTopic || "").trim().toLowerCase();
  const hintText = `${title} ${topic}`.trim();
  const isVetCategory = category.includes("veterinar");
  const hasConsultHint = /\b(consult|consultation|online vet|vet on call|tele|telehealth|video|audio|chat)\b/.test(hintText);
  if (!isVetCategory || !hasConsultHint) {
    return {
      bookingType: "service_booking",
      consultationMode: "",
      consultationTopic: "",
    };
  }

  let inferredMode = String(
    consultationMode || service?.consultationMode || service?.mode || "",
  ).trim().toLowerCase();
  if (!["audio", "video", "chat"].includes(inferredMode)) {
    if (/\bvideo\b/.test(hintText)) {
      inferredMode = "video";
    } else if (/\b(audio|voice|call)\b/.test(hintText)) {
      inferredMode = "audio";
    } else {
      inferredMode = "chat";
    }
  }

  return {
    bookingType: "vet_consultation",
    consultationMode: inferredMode,
    consultationTopic: String(
      consultationTopic || service?.consultationTopic || service?.title || "Online vet consultation",
    ).trim(),
  };
}

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window?.Razorpay) {
      resolve(true);
      return;
    }

    const existing = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
    if (existing) {
      existing.addEventListener("load", () => resolve(true), { once: true });
      existing.addEventListener("error", () => resolve(false), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function RazorpayButton({
  service,
  selectedPet,
  manualPetDetails,
  blockedDates = [],
  blockedSlots = [],
  bookingType = "service_booking",
  consultationMode = "",
  consultationTopic = "",
  requestedLanguage = "",
  preferredProviderId = "",
  preferredProviderEmail = "",
  disableDateSelection = false,
  hideDateInput = false,
  prefilledBookingDate = "",
  bookingEndDate = "",
  bookingSlot = "",
  externallyDisabled = false,
  disabledReason = "",
  buttonLabel = "Book Now",
  buttonStyle,
  containerStyle,
}) {
  const [bookingDate, setBookingDate] = useState(String(prefilledBookingDate || "").trim());
  const [selectedBookingSlot, setSelectedBookingSlot] = useState(
    () => String(bookingSlot || BOOKING_SLOT_OPTIONS[0] || "").trim(),
  );
  const [processing, setProcessing] = useState(false);
  const [lastError, setLastError] = useState("");
  const normalizedBlockedSlotKeys = useMemo(
    () =>
      (Array.isArray(blockedSlots) ? blockedSlots : [])
        .map((value) => String(value || "").trim().toLowerCase())
        .filter(Boolean),
    [blockedSlots],
  );
  const availableSlotOptions = useMemo(() => {
    if (disableDateSelection) return BOOKING_SLOT_OPTIONS;
    const targetDate = String(bookingDate || "").trim();
    if (!targetDate) return BOOKING_SLOT_OPTIONS;
    return BOOKING_SLOT_OPTIONS.filter((slot) => {
      const key = buildDateSlotKey(targetDate, slot);
      return key ? !normalizedBlockedSlotKeys.includes(key) : true;
    });
  }, [bookingDate, disableDateSelection, normalizedBlockedSlotKeys]);

  useEffect(() => {
    const nextDate = String(prefilledBookingDate || "").trim();
    if (!nextDate) return;
    setBookingDate(nextDate);
  }, [prefilledBookingDate]);

  useEffect(() => {
    const nextSlot = String(bookingSlot || "").trim();
    if (!nextSlot) return;
    setSelectedBookingSlot(nextSlot);
  }, [bookingSlot]);

  useEffect(() => {
    if (String(bookingSlot || "").trim()) return;
    if (!availableSlotOptions.length) {
      setSelectedBookingSlot("");
      return;
    }
    if (!availableSlotOptions.includes(selectedBookingSlot)) {
      setSelectedBookingSlot(availableSlotOptions[0]);
    }
  }, [availableSlotOptions, bookingSlot, selectedBookingSlot]);

  const handlePayment = async () => {
    if (processing) return;
    if (externallyDisabled) {
      if (disabledReason) {
        alert(disabledReason);
      }
      return;
    }
    try {
      setProcessing(true);
      setLastError("");
      const authUser = auth.currentUser;
      if (!authUser?.uid) {
        alert("Please login before booking.");
        return;
      }
      const storedOwnerIdentity =
        typeof window !== "undefined"
          ? String(window.localStorage.getItem("petaar_owner_identity") || "").trim()
          : "";
      const userEmail = String(
        authUser?.email ||
        authUser?.phoneNumber ||
        storedOwnerIdentity,
      ).trim();
      if (!userEmail) {
        alert("Please login before booking.");
        return;
      }

      const amount = Number(service?.price);
      if (!service?.title || !Number.isFinite(amount) || amount <= 0) {
        alert("Invalid service details. Please refresh and try again.");
        return;
      }
      const effectiveBookingDate = disableDateSelection ?
        new Date().toISOString().slice(0, 10) :
        bookingDate;
      const bookingIntent = resolveBookingIntent({
        service,
        bookingType,
        consultationMode,
        consultationTopic,
      });
      const effectiveBookingType = bookingIntent.bookingType;
      const effectiveConsultationMode = bookingIntent.consultationMode;
      const effectiveConsultationTopic = bookingIntent.consultationTopic;
      const effectiveBookingEndDate = String(bookingEndDate || "").trim();
      const effectiveBookingSlot = String(
        bookingSlot || (disableDateSelection ? "" : selectedBookingSlot),
      ).trim();
      if (!effectiveBookingDate) {
        alert("Please select booking date.");
        return;
      }
      if (!disableDateSelection && !effectiveBookingSlot) {
        alert("Please select booking slot.");
        return;
      }
      if (effectiveBookingEndDate && effectiveBookingEndDate < effectiveBookingDate) {
        alert("End date must be same or after start date.");
        return;
      }
      const selectedDate = new Date(`${effectiveBookingDate}T00:00:00`);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (Number.isNaN(selectedDate.getTime()) || selectedDate < today) {
        alert("Please select a valid future booking date.");
        return;
      }
      const maxDate = new Date();
      maxDate.setDate(maxDate.getDate() + 120);
      maxDate.setHours(0, 0, 0, 0);
      if (selectedDate > maxDate) {
        alert("Booking date is too far. Please select a date within 120 days.");
        return;
      }
      if (!disableDateSelection && Array.isArray(blockedDates) && blockedDates.includes(effectiveBookingDate)) {
        alert("Selected date is not available for this provider. Please choose another date.");
        return;
      }
      if (!disableDateSelection && effectiveBookingSlot) {
        const slotKey = buildDateSlotKey(effectiveBookingDate, effectiveBookingSlot);
        if (slotKey && normalizedBlockedSlotKeys.includes(slotKey)) {
          alert("Selected slot is not available for this provider. Please choose another slot.");
          return;
        }
      }
      const extraPet = manualPetDetails && typeof manualPetDetails === "object" ? manualPetDetails : {};
      const mergedPetDetails = {
        id: selectedPet?.id || "",
        name: selectedPet?.name || extraPet.name || "",
        species: selectedPet?.species || extraPet.species || "",
        breed: selectedPet?.breed || extraPet.breed || "",
        age: selectedPet?.age || extraPet.age || "",
        weight: selectedPet?.weight || extraPet.weight || "",
        allergies: selectedPet?.allergies || extraPet.allergies || "",
        medications: selectedPet?.medications || extraPet.medications || "",
        vaccinationStatus: selectedPet?.vaccinationStatus || extraPet.vaccinationStatus || "",
        symptoms: extraPet.symptoms || "",
        notes: [selectedPet?.notes || "", extraPet.notes || ""].filter(Boolean).join(" | "),
      };

      const orderRes = await fetch(
        CREATE_ORDER_URL,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ amount })
        }
      );

      if (!orderRes.ok) {
        let errorMessage = `Order creation failed (${orderRes.status})`;
        try {
          const errorData = await orderRes.json();
          if (errorData?.error) {
            errorMessage = errorData.error;
          }
          if (errorData?.details) {
            errorMessage += `: ${errorData.details}`;
          }
        } catch (_) {}
        throw new Error(errorMessage);
      }

      const order = await orderRes.json();
      if (!order?.id || !order?.amount) {
        throw new Error("Invalid order response");
      }

      const sdkLoaded = await loadRazorpayScript();
      if (!sdkLoaded || !window?.Razorpay) {
        throw new Error("Razorpay SDK failed to load");
      }

      const options = {
        key: order.key_id || RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: "INR",
        name: "Petaar",
        description: service.title,
        order_id: order.id,
        prefill: {
          email: userEmail.includes("@") ? userEmail : "",
        },

        handler: async function (response) {
          try {
            const verifyRes = await fetch(
              VERIFY_PAYMENT_URL,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  ...response,
                  ownerEmail: userEmail,
                  // CONSULTATION CHAT: Send owner UID for secure owner/vet chat access mapping.
                  ownerUid: authUser?.uid || "",
                  serviceId: service.id || "",
                  serviceTitle: service.title || "",
                  providerEmail: service.providerEmail || "",
                  providerId: service.providerId || "",
                  city: service.city || "",
                  category: service.category || "",
                  bookingDate: effectiveBookingDate,
                  bookingEndDate: effectiveBookingEndDate,
                  amount,
                  petDetails: mergedPetDetails,
                  bookingType: effectiveBookingType,
                  consultationMode: effectiveConsultationMode,
                  consultationTopic: effectiveConsultationTopic,
                  requestedLanguage,
                  bookingSlot: effectiveBookingSlot,
                  preferredProviderId,
                  preferredProviderEmail,
                })
              }
            );

            if (!verifyRes.ok) {
              let errorMessage = `Verify failed (${verifyRes.status})`;
              try {
                const errorData = await verifyRes.json();
                if (errorData?.error) {
                  errorMessage = errorData.error;
                }
                if (errorData?.details) {
                  errorMessage += `: ${errorData.details}`;
                }
              } catch (_) {}
              throw new Error(errorMessage);
            }

            const result = await verifyRes.json();

            if (result.success) {
              let hasAssignedProvider = Boolean(result.assignedProviderEmail);
              if (!hasAssignedProvider && result.bookingId) {
                try {
                  const token = await auth.currentUser?.getIdToken();
                  if (token) {
                    const retryRes = await fetch(REASSIGN_BOOKING_URL, {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`,
                      },
                      body: JSON.stringify({ bookingId: result.bookingId }),
                    });
                    const retryData = await retryRes.json().catch(() => ({}));
                    if (retryRes.ok && retryData?.success && retryData?.reassigned) {
                      hasAssignedProvider = true;
                    }
                  }
                } catch (_) {
                  // Assignment retry is best-effort; verify flow remains successful.
                }
              }
              const assignmentNote = hasAssignedProvider ?
                " Provider assigned successfully." :
                " We are finding an available provider.";
              alert(`✅ Payment successful & booking saved.${assignmentNote}`);
              // CONSULTATION CHAT: Redirect paid vet consultations directly into in-app chat.
              if (effectiveBookingType === "vet_consultation" && result.bookingId) {
                window.location.hash = `#/consultation/${encodeURIComponent(result.bookingId)}`;
                return;
              }
            } else {
              alert("❌ Payment verification failed");
            }
          } catch (error) {
            alert(`❌ Payment verification failed: ${error.message}`);
          } finally {
            setProcessing(false);
          }
        },

        theme: { color: "#3399cc" }
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response) => {
        const reason = response?.error?.description || response?.error?.reason || "Payment failed";
        setLastError(reason);
        alert(`❌ Payment failed: ${reason}`);
      });
      rzp.on("modal.closed", () => {
        setProcessing(false);
      });
      rzp.open();

    } catch (err) {
      console.error(err);
      setLastError(err.message || "Payment failed");
      alert(`❌ Payment failed: ${err.message}`);
      setProcessing(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        flexWrap: "wrap",
        alignItems: "center",
        ...(containerStyle && typeof containerStyle === "object" ? containerStyle : {}),
      }}
    >
      {!disableDateSelection && !hideDateInput && (
        <>
          <input
            type="date"
            min={new Date().toISOString().slice(0, 10)}
            value={bookingDate}
            onChange={(e) => setBookingDate(e.target.value)}
            disabled={processing}
          />
          <select
            value={selectedBookingSlot}
            onChange={(e) => setSelectedBookingSlot(e.target.value)}
            disabled={processing || availableSlotOptions.length === 0}
          >
            <option value="">Select Slot</option>
            {availableSlotOptions.map((slot) => (
              <option key={slot} value={slot}>{slot}</option>
            ))}
          </select>
        </>
      )}
      {!disableDateSelection && !hideDateInput && availableSlotOptions.length === 0 && (
        <small style={{ color: "#b91c1c", display: "block", flexBasis: "100%" }}>
          No slots available on selected date. Please choose another date.
        </small>
      )}
      <button
        onClick={handlePayment}
        disabled={processing || externallyDisabled}
        style={buttonStyle && typeof buttonStyle === "object" ? buttonStyle : undefined}
      >
        {processing ? "Processing..." : buttonLabel}
      </button>
      {externallyDisabled && disabledReason && !lastError && (
        <small style={{ color: "#b91c1c", display: "block", flexBasis: "100%" }}>
          {disabledReason}
        </small>
      )}
      {lastError && (
        <small style={{ color: "#b91c1c", display: "block", flexBasis: "100%" }}>
          Last payment error: {lastError}
        </small>
      )}
    </div>
  );
}
